import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { LineReveal, EASE } from "@/components/site/Reveal";
import { IMG } from "@/lib/content";

const STATS = [
  ["53–87%", "Average power savings"],
  ["$0", "Capital outlay"],
  ["2–2.5 yrs", "Typical payback"],
];

const useTilt = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = { stiffness: 110, damping: 18 };
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), spring);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), spring);
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => { mx.set(0); my.set(0); };
  return { rotateX, rotateY, onMouseMove, onMouseLeave };
};

const HeroVisual = ({ imgY }) => {
  const { rotateX, rotateY, onMouseMove, onMouseLeave } = useTilt();
  return (
    <div className="lg:col-span-5 relative" style={{ perspective: 1200 }} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
        className="relative"
      >
        <div className="relative aspect-[4/3] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden clip-diagonal bg-[var(--navy)]">
          <motion.img style={{ y: imgY, scale: 1.18 }} src={IMG.heroWash} alt="Car being washed inside a wash bay" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/10 to-transparent" />
          <div className="absolute inset-0 bg-[var(--solar)]/10 mix-blend-color" />
        </div>
        <div style={{ transform: "translateZ(70px)" }} data-testid="hero-savings-card" className="absolute left-3 sm:-left-8 bottom-5 sm:bottom-10 rounded-xl border border-white/15 bg-[var(--ink)]/80 backdrop-blur-xl px-5 py-4 shadow-2xl">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--solar-light)]">100kW carwash system</p>
          <p className="font-display font-black text-4xl text-white mt-1">$6,000<span className="text-lg text-slate-300 font-bold">/month</span></p>
          <p className="text-sm text-slate-300">wiped off your power bill</p>
        </div>
        <div style={{ transform: "translateZ(50px)" }} className="absolute right-3 -top-4 rounded-full bg-[var(--sun)] text-[var(--ink)] px-4 py-2 font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2 shadow-xl">
          <Zap size={15} fill="currentColor" /> 2026 Govt incentives
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const sunY = useTransform(scrollYProgress, [0, 1], ["0%", "-45%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden bg-[var(--ink)] pt-32 lg:pt-40 pb-16 lg:pb-24">
      <div className="hero-grid absolute inset-0" aria-hidden />
      <motion.div style={{ y: sunY }} className="sun-orb absolute -top-48 -left-48 w-[40rem] h-[40rem]" aria-hidden />
      <div className="relative mx-auto max-w-[1440px] px-5 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }} className="eyebrow">
            Car wash solar &amp; battery experts · Since 2013
          </motion.p>
          <LineReveal
            as="h1"
            testId="hero-headline"
            delay={0.15}
            lines={["Slash power", "costs by", <span className="text-[var(--sun)]">over 50%.</span>]}
            className="mt-6 font-display font-black uppercase text-white leading-[0.84] tracking-[-0.01em] text-[3.9rem] sm:text-8xl lg:text-[7.5rem] xl:text-[9rem]"
          />
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9, ease: EASE }} className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            Still haven't installed solar in your carwash yet? Here's how you can — safely, simply and affordably. Your power savings can fund your entire solar + battery system.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }} className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/savings" data-testid="hero-calc-btn" className="group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--sun)] text-[var(--ink)] px-8 py-4 font-display font-bold uppercase tracking-wider text-lg shadow-[0_0_40px_rgba(255,199,0,0.25)] transition-colors hover:bg-[var(--sun-deep)]">
              Calculate my savings <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" size={20} />
            </Link>
            <Link to="/contact" data-testid="hero-quote-btn" className="inline-flex items-center justify-center rounded-full border border-white/25 px-8 py-4 font-display font-bold uppercase tracking-wider text-lg text-white transition-colors hover:border-[var(--solar-light)] hover:bg-[var(--solar)]/10">
              Free site assessment
            </Link>
          </motion.div>
          <motion.dl initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }} className="mt-12 grid grid-cols-3 gap-4 max-w-xl border-t border-white/10 pt-6">
            {STATS.map(([v, l]) => (
              <div key={l}>
                <dt className="font-display font-black text-2xl sm:text-4xl text-white">{v}</dt>
                <dd className="text-xs sm:text-sm text-slate-400 mt-1">{l}</dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>
        <HeroVisual imgY={imgY} />
      </div>
    </section>
  );
};
